import { useQuery } from '@tanstack/react-query'
import { collectionGroup, getDocs } from 'firebase/firestore'
import { db, hasValidFirebaseEnv } from '@/lib/firebase'
import { DEFAULT_PARLENT_WATCH_MODELS, isParlentWatch } from '@/lib/watch-models'
import type { FirestoreDate } from '@/types'

export interface OwnedWatch {
  address: string
  name: string | null
  model: string | null
  firmwareVersion: string | null
  mcuType: string | null
  /** OS ponsel saat pairing ('android' | 'ios'). Bukan platform chip jam. */
  phonePlatform: string | null
  isParlent: boolean
  firstConnectedAt: FirestoreDate
  lastConnected: FirestoreDate
  connectionCount: number | null
  battery: number | null
}

/** Satu unit jam fisik, dikenali dari MAC address, beserta akun pemakainya. */
export interface RegisteredWatch {
  address: string
  name: string | null
  model: string | null
  firmwareVersion: string | null
  isParlent: boolean
  /** Bisa lebih dari satu: jam demo sering dipasangkan beberapa akun. */
  ownerUids: string[]
  lastConnected: FirestoreDate
}

export interface WatchOwnership {
  /** uid → semua jam yang pernah dipakai user itu. */
  byUser: Map<string, OwnedWatch[]>
  /** MAC address → unit jam. Satu baris per jam fisik, bukan per akun. */
  byAddress: Map<string, RegisteredWatch>
  /** Jumlah unit jam Parlent unik — beda dari jumlah akun pemakainya. */
  parlentUnitCount: number
  /** User dengan minimal satu jam Parlent (boleh punya merek lain juga). */
  parlentUserIds: Set<string>
  /** User yang punya jam, tapi tidak satu pun cocok whitelist. */
  otherUserIds: Set<string>
  /** User yang punya jam tapi namanya belum pernah terkirim (app versi lama). */
  unknownUserIds: Set<string>
  totalDeviceDocs: number
}

const EMPTY_OWNERSHIP: WatchOwnership = {
  byUser: new Map(),
  byAddress: new Map(),
  parlentUnitCount: 0,
  parlentUserIds: new Set(),
  otherUserIds: new Set(),
  unknownUserIds: new Set(),
  totalDeviceDocs: 0,
}

/**
 * Baca seluruh doc di collection group `devices` lalu kelompokkan per user.
 *
 * Firestore tidak bisa melakukan join, jadi klasifikasi dilakukan di klien.
 * Ini aman selama jumlah device masih ribuan; kalau tumbuh jauh lebih besar,
 * pindahkan agregasi ke Cloud Function yang menulis ringkasan ke doc user.
 */
export function useWatchOwnership(
  whitelist: string[] = DEFAULT_PARLENT_WATCH_MODELS,
) {
  return useQuery<WatchOwnership>({
    queryKey: ['watch-ownership', whitelist.join('|')],
    queryFn: async () => {
      if (!hasValidFirebaseEnv) return EMPTY_OWNERSHIP

      const snapshot = await getDocs(collectionGroup(db, 'devices'))
      const byUser = new Map<string, OwnedWatch[]>()
      const byAddress = new Map<string, RegisteredWatch>()

      snapshot.docs.forEach((item) => {
        const uid = item.ref.parent.parent?.id
        if (!uid) return

        const data = item.data() as Record<string, unknown>
        const name = (data.name as string | undefined)?.trim() || null

        const watch: OwnedWatch = {
          address: (data.address as string | undefined) ?? item.id,
          name,
          model: (data.model as string | undefined) ?? null,
          firmwareVersion: (data.firmwareVersion as string | undefined) ?? null,
          mcuType: (data.mcuType as string | undefined) ?? null,
          phonePlatform: (data.phonePlatform as string | undefined) ?? null,
          isParlent: isParlentWatch(name, whitelist),
          firstConnectedAt: (data.firstConnectedAt as FirestoreDate) ?? null,
          lastConnected:
            (data.lastConnected as FirestoreDate) ??
            (data.batteryUpdatedAt as FirestoreDate) ??
            null,
          connectionCount: (data.connectionCount as number | undefined) ?? null,
          battery: (data.battery as number | undefined) ?? null,
        }

        const list = byUser.get(uid)
        if (list) {
          list.push(watch)
        } else {
          byUser.set(uid, [watch])
        }

        // Kelompokkan per MAC: satu jam fisik bisa dipasangkan banyak akun
        // (unit demo yang berpindah tangan), dan itu tetap satu unit.
        const existing = byAddress.get(watch.address)
        if (existing) {
          if (!existing.ownerUids.includes(uid)) existing.ownerUids.push(uid)
          // Doc mana pun boleh melengkapi identitas yang belum terisi.
          existing.name = existing.name ?? watch.name
          existing.model = existing.model ?? watch.model
          existing.firmwareVersion = existing.firmwareVersion ?? watch.firmwareVersion
          existing.isParlent = existing.isParlent || watch.isParlent
          existing.lastConnected = existing.lastConnected ?? watch.lastConnected
        } else {
          byAddress.set(watch.address, {
            address: watch.address,
            name: watch.name,
            model: watch.model,
            firmwareVersion: watch.firmwareVersion,
            isParlent: watch.isParlent,
            ownerUids: [uid],
            lastConnected: watch.lastConnected,
          })
        }
      })

      const parlentUserIds = new Set<string>()
      const otherUserIds = new Set<string>()
      const unknownUserIds = new Set<string>()

      byUser.forEach((watches, uid) => {
        // Satu jam Parlent sudah cukup — user boleh punya jam merek lain juga.
        if (watches.some((watch) => watch.isParlent)) {
          parlentUserIds.add(uid)
          return
        }
        if (watches.some((watch) => watch.name)) {
          otherUserIds.add(uid)
          return
        }
        unknownUserIds.add(uid)
      })

      let parlentUnitCount = 0
      byAddress.forEach((watch) => {
        if (watch.isParlent) parlentUnitCount += 1
      })

      return {
        byUser,
        byAddress,
        parlentUnitCount,
        parlentUserIds,
        otherUserIds,
        unknownUserIds,
        totalDeviceDocs: snapshot.size,
      }
    },
    staleTime: 2 * 60 * 1000,
  })
}
